/**
 * Item icons, portrait frames and FX — Style Bible §5, §5.1, §7.
 *
 * Item icons are generated from (slot, rarity) rather than drawn individually:
 * 28 items would be 28 illustrations, but they only need to be *distinguishable
 * and readable at 48 px*, which a shape-plus-rarity-ring system does better than
 * small raster art anyway.
 *
 * Print direction (Style Bible v2.0): nothing in this file is lit. A 1980s press
 * had a black key plate and a few spot inks, so a face here is bare cream paper
 * with an orange screen over it, the shadow side is one more screen, and the only
 * "highlight" is paper that was never inked. Colour plates are pushed a hair off
 * the key line — once, in one direction for the whole portrait, because a face
 * whose parts each shift separately reads as broken rather than as misprinted.
 */

import {
  HALFTONE,
  HALFTONE_ANGLE,
  ICON_SPEC,
  INK,
  LINE,
  MISREGISTRATION,
  PAPER,
  PORTRAIT_SPEC,
  RARITY,
  ROLE_INK,
  SCREEN_ANGLE,
  SPOT,
  type HalftoneDensity,
  type PortraitRole,
  type Rarity,
} from './tokens.js';
import {
  ArtRng,
  agedEdges,
  circle,
  defs,
  ellipse,
  group,
  halftonePattern,
  misregisterOffset,
  misregistered,
  n,
  paperGrain,
  path,
  polygon,
  svg,
  tag,
  text,
} from './svg.js';

export type ItemSlot = 'weapon' | 'armor' | 'trinket' | 'consumable' | 'quest' | 'material';

/** One silhouette per slot. Readable at 48 px, which is the real constraint. */
const SLOT_GLYPH: Readonly<Record<ItemSlot, string>> = {
  weapon: 'M50 14 L56 14 L58 62 L53 74 L48 62 Z M42 60 h22 v6 h-22 Z M50 66 h6 v22 h-6 Z',
  armor: 'M32 26 L53 18 L74 26 V56 Q53 84 32 56 Z',
  trinket: 'M53 20 a16 16 0 1 1 -0.1 0 M53 36 v14 M40 50 h26 l-4 34 h-18 Z',
  consumable: 'M44 18 h18 v12 l8 14 v34 a8 8 0 0 1 -8 8 h-18 a8 8 0 0 1 -8 -8 V44 l8 -14 Z',
  quest: 'M28 22 h50 v58 l-25 -14 -25 14 Z',
  material: 'M53 18 L80 40 L70 78 H36 L26 40 Z',
};

/** Pattern ids. Short and stable, so output stays byte-identical for a seed. */
const P = {
  grain: 'ig',
  iconScreen: 'ics',
  skin: 'psk',
  faceShade: 'pfs',
  cloth: 'pcl',
  hairScreen: 'phs',
  halo: 'phl',
  fxScreen: 'fxs',
} as const;

/** The key plate: outlines only, printed over every colour plate. */
function keyline(weight: number) {
  return {
    fill: 'none',
    stroke: INK.key,
    'stroke-width': weight,
    'stroke-linejoin': 'round',
  } as const;
}

export interface IconRenderOptions {
  readonly slot: ItemSlot;
  readonly rarity: Rarity;
  readonly size?: number;
  readonly seed?: string;
}

export function renderItemIcon(opts: IconRenderOptions): string {
  const size = opts.size ?? ICON_SPEC.size;
  const { slot, rarity } = opts;
  const rc = RARITY[rarity].color;
  const s = size / 128;
  const seed = opts.seed ?? `${slot}:${rarity}`;
  const cx = size / 2;
  const r = size * 0.46;

  const glyphTransform = `translate(${n(size * 0.09)} ${n(size * 0.06)}) scale(${n((size * 0.82) / 106)})`;

  /*
   * Colour plate: the disc is bare card screened back in the rarity ink, and the
   * glyph is that ink laid flat. There is no specular dot — the previous pass had
   * one, and a printed icon has no highlight to catch.
   */
  const plate =
    circle({ cx, cy: cx, r, fill: PAPER.card }) +
    circle({ cx, cy: cx, r, fill: `url(#${P.iconScreen})` }) +
    group({ transform: glyphTransform }, path(SLOT_GLYPH[slot], { fill: rc }));

  const key =
    circle({ cx, cy: cx, r, fill: 'none', stroke: rc, 'stroke-width': LINE.structure * s * 1.6 }) +
    circle({ cx, cy: cx, r, ...keyline(LINE.detail * s) }) +
    group(
      { transform: glyphTransform },
      path(SLOT_GLYPH[slot], keyline(LINE.structure * 1.4)),
    ) +
    // Legendary and above get a second ring, matching the card frame escalation.
    (rarity === 'legendary' || rarity === 'mythic'
      ? circle({ cx, cy: cx, r: size * 0.41, ...keyline(LINE.detail * s) })
      : '');

  const screens = halftonePattern(
    P.iconScreen,
    rc,
    HALFTONE.light.dot * s,
    HALFTONE.light.grid * s,
    HALFTONE_ANGLE,
  );

  return svg(
    size,
    size,
    defs(screens) + misregistered(`icon:${seed}`, size, MISREGISTRATION, plate) + key,
  );
}

/**
 * Portrait frame plus a procedural placeholder bust.
 *
 * Portraits are the one asset class where AI raster art genuinely wins (faces),
 * so this is a frame and a stand-in, not a substitute. The `slot` placeholder
 * keeps the game playable and the layout final while illustration is pending.
 */
export interface PortraitOptions {
  readonly name: string;
  readonly classOrRole?: PortraitRole;
  readonly size?: number;
  /** Drives every facial feature. Pass the NPC id so a character is stable. */
  readonly seed?: string;
  readonly artHref?: string;
  /** Style Bible §5: expression is an overlay, never a second portrait. */
  readonly expression?: 'neutral' | 'warm' | 'wary' | 'hostile' | 'broken';
  /** Overrides derived from content, so an author can pin a look. */
  readonly age?: 'young' | 'middle' | 'old';
  readonly build?: 'slight' | 'average' | 'broad';
  /** Draw the name plate inside the frame. Off where the UI prints it alongside. */
  readonly showName?: boolean;
}

/**
 * Feature sets. Five NPCs rendering as the same silhouette is a bug, not a
 * placeholder: the player is supposed to recognise a face they have met before,
 * and NPC memory is invisible if everyone looks identical.
 *
 * Each feature is picked deterministically from the seed, so a character's face
 * never changes between sessions.
 *
 * Skin is a halftone DENSITY, not a colour (Style Bible §5.1). A press printing
 * this game had no flesh plate; it had an orange one, and a face was however many
 * dots of it the screen carried. Four densities give four readable skin tones
 * without inventing a single new hex value.
 */
const SKIN_DENSITIES: readonly HalftoneDensity[] = ['light', 'mid', 'dark', 'shadow'];

/** Hair is one flat ink. Nothing here is a blend of two (Style Bible §5.1). */
const HAIR_INKS = [INK.key, INK.faded, PAPER.aged, SPOT.orange, SPOT.yellow, SPOT.red] as const;
/** What an old head prints as: the ink runs out, it does not turn grey-brown. */
const OLD_HAIR_INKS = [PAPER.aged, INK.faded] as const;

type HairStyle = 'bald' | 'short' | 'long' | 'tied' | 'wild' | 'hooded' | 'veiled';
type FacialHair = 'none' | 'stubble' | 'beard' | 'longBeard' | 'moustache';

const HAIR_STYLES: readonly HairStyle[] = ['bald', 'short', 'long', 'tied', 'wild', 'hooded', 'veiled'];
const FACIAL_HAIR: readonly FacialHair[] = ['none', 'stubble', 'beard', 'longBeard', 'moustache'];

/**
 * One source of truth for where the face sits.
 *
 * Hair, facial hair and the expression overlay all key off this. Previously each
 * derived its own fractions of `size`, so changing the head size slid the eyes
 * out from under the fringe — the classic bug this struct exists to prevent.
 */
interface FaceGeom {
  readonly cx: number;
  readonly headY: number;
  readonly headR: number;
  readonly headRy: number;
  readonly eyeY: number;
  readonly eyeDx: number;
  readonly mouthY: number;
  readonly browY: number;
  readonly chinY: number;
  readonly shoulderY: number;
  readonly s: number;
}

function faceGeom(size: number, f: Features): FaceGeom {
  const s = size / 512;
  // A portrait bust: the head carries the frame. At 0.135 it read as a doll on a
  // paper bag, which is what the first pass shipped.
  const headR = size * 0.2 * f.faceWidth;
  const headRy = headR * 1.1;
  const headY = size * 0.4;
  return {
    cx: size / 2,
    headY,
    headR,
    headRy,
    eyeY: headY - headRy * 0.1,
    eyeDx: headR * 0.4,
    mouthY: headY + headRy * 0.45,
    browY: headY - headRy * 0.34,
    chinY: headY + headRy,
    shoulderY: headY + headRy * 1.05,
    s,
  };
}

interface Features {
  /** Halftone density of the orange plate — this game's only skin tone control. */
  skin: HalftoneDensity;
  hair: string;
  hairStyle: HairStyle;
  facialHair: FacialHair;
  age: 'young' | 'middle' | 'old';
  build: 'slight' | 'average' | 'broad';
  /** Face width multiplier, so heads are not all the same oval. */
  faceWidth: number;
  browAngle: number;
  hasScar: boolean;
  hasEarring: boolean;
}

function deriveFeatures(seed: string, opts: PortraitOptions): Features {
  const rng = new ArtRng(`features:${seed}`);
  const age = opts.age ?? (['young', 'middle', 'old'] as const)[Math.floor(rng.next() * 3)]!;
  const build = opts.build ?? (['slight', 'average', 'broad'] as const)[Math.floor(rng.next() * 3)]!;
  const hairStyle = rng.pick(HAIR_STYLES);
  // Older characters grey; the young rarely have long beards.
  const hair = age === 'old' && rng.next() < 0.65 ? rng.pick(OLD_HAIR_INKS) : rng.pick(HAIR_INKS);
  let facialHair = rng.pick(FACIAL_HAIR);
  if (age === 'young' && facialHair === 'longBeard') facialHair = 'stubble';

  return {
    skin: rng.pick(SKIN_DENSITIES),
    hair,
    hairStyle,
    facialHair,
    age,
    build,
    faceWidth: rng.range(0.88, 1.12),
    browAngle: rng.range(-6, 6),
    hasScar: rng.next() < 0.22,
    hasEarring: rng.next() < 0.25,
  };
}

/**
 * A shape that has to be printed twice: once on a colour plate, once on the key
 * plate. Keeping them apart is the whole trick — the colour is what shifts, the
 * black line is what stays put.
 */
interface Piece {
  readonly d: string;
  readonly fill: string;
  /** Key-line weight. `0` means this piece prints with no outline at all. */
  readonly weight: number;
}

const platesOf = (pieces: readonly Piece[]): string =>
  pieces.map((p) => path(p.d, { fill: p.fill })).join('');
const keysOf = (pieces: readonly Piece[]): string =>
  pieces
    .filter((p) => p.weight > 0)
    .map((p) => path(p.d, keyline(p.weight)))
    .join('');

/**
 * Hair in two layers: mass behind the head, fringe in front. The fringe's lower
 * edge is pinned above `browY` so no style can cover the eyes.
 */
function hairLayers(f: Features, g: FaceGeom): { back: Piece[]; front: Piece[] } {
  const { cx, headY, headR, headRy, browY, s } = g;
  const w = LINE.structure * s;
  // Cap: arc over the crown, returning along a fringe line above the brow.
  const fringeY = browY - headRy * 0.06;
  const capPath = (widen = 1, dip = 0.0): string =>
    `M${n(cx - headR * widen)} ${n(fringeY)}` +
    ` A${n(headR * widen)} ${n(headRy * widen)} 0 0 1 ${n(cx + headR * widen)} ${n(fringeY)}` +
    ` Q${n(cx)} ${n(fringeY + headRy * dip)} ${n(cx - headR * widen)} ${n(fringeY)} Z`;
  const cap = (widen = 1, dip = 0.0): Piece => ({ d: capPath(widen, dip), fill: f.hair, weight: w });

  switch (f.hairStyle) {
    case 'bald':
      return { back: [], front: [] };
    case 'short':
      return { back: [], front: [cap(1.04, 0.1)] };
    case 'long':
      return {
        back: [
          {
            d:
              `M${n(cx - headR * 1.08)} ${n(headY - headRy * 0.3)}` +
              ` q${n(-headR * 0.16)} ${n(headRy * 1.9)} ${n(headR * 0.2)} ${n(headRy * 2.0)}` +
              ` h${n(headR * 1.76)}` +
              ` q${n(headR * 0.36)} ${n(-headRy * 0.1)} ${n(headR * 0.2)} ${n(-headRy * 2.0)} Z`,
            fill: f.hair,
            weight: w,
          },
        ],
        front: [cap(1.06, 0.16)],
      };
    case 'tied':
      return {
        back: [
          {
            d:
              `M${n(cx + headR * 0.8)} ${n(headY - headRy * 0.3)}` +
              ` q${n(headR * 0.75)} ${n(headRy * 0.35)} ${n(headR * 0.42)} ${n(headRy * 1.25)}` +
              ` q${n(-headR * 0.3)} ${n(headRy * 0.1)} ${n(-headR * 0.48)} ${n(-headRy * 0.3)} Z`,
            fill: f.hair,
            weight: w,
          },
        ],
        front: [cap(1.03, 0.04)],
      };
    case 'wild':
      return {
        // Spikes as outlined wedges rather than strokes: a stroke has no key line
        // of its own, and every shape in this system carries one.
        back: Array.from({ length: 9 }, (_, i): Piece => {
          const a = Math.PI * (1 + i / 8);
          const x = cx + Math.cos(a) * headR * 0.96;
          const y = headY + Math.sin(a) * headRy * 0.96;
          const tipX = cx + Math.cos(a) * headR * 1.38;
          const tipY = headY + Math.sin(a) * headRy * 1.38;
          const nx = -Math.sin(a) * headR * 0.13;
          const ny = Math.cos(a) * headRy * 0.13;
          return {
            d: `M${n(x + nx)} ${n(y + ny)} L${n(tipX)} ${n(tipY)} L${n(x - nx)} ${n(y - ny)} Z`,
            fill: f.hair,
            weight: LINE.detail * s,
          };
        }),
        front: [cap(1.02, 0.02)],
      };
    case 'hooded':
      return {
        // Cowl behind the head; it frames the face instead of crossing it.
        back: [
          {
            d:
              `M${n(cx - headR * 1.34)} ${n(g.chinY + headRy * 0.1)}` +
              ` Q${n(cx - headR * 1.44)} ${n(headY - headRy * 1.6)} ${n(cx)} ${n(headY - headRy * 1.54)}` +
              ` Q${n(cx + headR * 1.44)} ${n(headY - headRy * 1.6)} ${n(cx + headR * 1.34)} ${n(g.chinY + headRy * 0.1)} Z`,
            fill: PAPER.box,
            weight: LINE.outline * s,
          },
        ],
        // Side flaps only, hugging the outer edge of the head.
        front: [
          {
            d:
              `M${n(cx - headR * 1.34)} ${n(g.chinY + headRy * 0.1)}` +
              ` Q${n(cx - headR * 1.36)} ${n(headY - headRy * 0.9)} ${n(cx - headR * 0.86)} ${n(headY - headRy * 0.82)}` +
              ` Q${n(cx - headR * 1.02)} ${n(headY)} ${n(cx - headR * 0.96)} ${n(g.chinY + headRy * 0.04)} Z`,
            fill: PAPER.box,
            weight: w,
          },
          {
            d:
              `M${n(cx + headR * 1.34)} ${n(g.chinY + headRy * 0.1)}` +
              ` Q${n(cx + headR * 1.36)} ${n(headY - headRy * 0.9)} ${n(cx + headR * 0.86)} ${n(headY - headRy * 0.82)}` +
              ` Q${n(cx + headR * 1.02)} ${n(headY)} ${n(cx + headR * 0.96)} ${n(g.chinY + headRy * 0.04)} Z`,
            // The far flap is the same ink, screened — one plate, two values.
            fill: `url(#${P.cloth})`,
            weight: w,
          },
          // A screened band under the cowl edge, kept above the brow line. This
          // was a translucent black wash; a press cannot print one.
          {
            d:
              `M${n(cx - headR * 0.94)} ${n(fringeY - headRy * 0.02)}` +
              ` A${n(headR * 0.94)} ${n(headRy * 0.94)} 0 0 1 ${n(cx + headR * 0.94)} ${n(fringeY - headRy * 0.02)}` +
              ` Q${n(cx)} ${n(fringeY + headRy * 0.1)} ${n(cx - headR * 0.94)} ${n(fringeY - headRy * 0.02)} Z`,
            fill: `url(#${P.faceShade})`,
            weight: 0,
          },
        ],
      };
    case 'veiled':
      return {
        back: [
          {
            d:
              `M${n(cx - headR * 1.2)} ${n(headY - headRy * 0.2)}` +
              ` q${n(-headR * 0.1)} ${n(headRy * 1.9)} ${n(headR * 0.25)} ${n(headRy * 2.0)}` +
              ` h${n(headR * 1.9)}` +
              ` q${n(headR * 0.35)} ${n(-headRy * 0.1)} ${n(headR * 0.25)} ${n(-headRy * 2.0)} Z`,
            fill: `url(#${P.hairScreen})`,
            weight: w,
          },
        ],
        front: [
          {
            d:
              `M${n(cx - headR * 1.16)} ${n(fringeY + headRy * 0.12)}` +
              ` A${n(headR * 1.16)} ${n(headRy * 1.16)} 0 0 1 ${n(cx + headR * 1.16)} ${n(fringeY + headRy * 0.12)}` +
              ` q${n(-headR * 0.3)} ${n(-headRy * 0.24)} ${n(-headR * 1.16)} ${n(-headRy * 0.24)}` +
              ` q${n(-headR * 0.86)} 0 ${n(-headR * 1.16)} ${n(headRy * 0.24)} Z`,
            fill: f.hair,
            weight: w,
          },
        ],
      };
  }
}

function facialHairPieces(f: Features, g: FaceGeom): Piece[] {
  const { cx, headY, headR, headRy, mouthY, s } = g;
  const w = LINE.structure * s * 0.8;
  // Jaw arc, shared by every beard so they all sit on the same face.
  const jaw = (drop: number): string =>
    `M${n(cx - headR * 0.86)} ${n(headY + headRy * 0.16)}` +
    ` a${n(headR * 0.88)} ${n(headRy * (0.84 + drop))} 0 0 0 ${n(headR * 1.72)} 0`;

  switch (f.facialHair) {
    case 'none':
      return [];
    case 'stubble':
      // Unshaven is the same ink at a lighter screen — not the same ink at 30%
      // opacity, which is what the old pass did and a press cannot do.
      return [{ d: `${jaw(0)} Z`, fill: `url(#${P.hairScreen})`, weight: 0 }];
    case 'beard':
      return [{ d: `${jaw(0.18)} Z`, fill: f.hair, weight: w }];
    case 'longBeard':
      // Tapers to a point below the chin rather than widening into a bib.
      return [
        {
          d:
            `M${n(cx - headR * 0.84)} ${n(headY + headRy * 0.14)}` +
            ` Q${n(cx - headR * 0.6)} ${n(headY + headRy * 1.34)} ${n(cx)} ${n(headY + headRy * 1.52)}` +
            ` Q${n(cx + headR * 0.6)} ${n(headY + headRy * 1.34)} ${n(cx + headR * 0.84)} ${n(headY + headRy * 0.14)}` +
            ` a${n(headR * 0.84)} ${n(headRy * 0.78)} 0 0 1 ${n(-headR * 1.68)} 0 Z`,
          fill: f.hair,
          weight: w,
        },
      ];
    case 'moustache':
      return [
        {
          d:
            `M${n(cx - headR * 0.4)} ${n(mouthY - headRy * 0.12)}` +
            ` q${n(headR * 0.4)} ${n(headRy * 0.16)} ${n(headR * 0.8)} 0` +
            ` q${n(-headR * 0.4)} ${n(headRy * 0.22)} ${n(-headR * 0.8)} 0 Z`,
          fill: f.hair,
          weight: w,
        },
      ];
  }
}

/**
 * Class silhouettes per Style Bible §5: shoulder line plus one prop, varied per
 * character by build so two people never share an outline.
 *
 * Returns the colour plate and the key plate separately. They are composited by
 * the caller so that every plate in the portrait shifts by the same offset.
 */
function bustSilhouette(
  kind: PortraitRole,
  size: number,
  f: Features,
  g: FaceGeom,
): { plate: string; key: string } {
  const { cx, headY, headR, headRy, shoulderY, s } = g;
  const outline = LINE.outline * s;
  const structure = LINE.structure * s;
  const detail = LINE.detail * s;
  const buildScale = f.build === 'slight' ? 0.86 : f.build === 'broad' ? 1.16 : 1;
  const roleInk = ROLE_INK[kind];
  const hair = hairLayers(f, g);

  // --- torso ------------------------------------------------------------
  const shoulderPts = (halfTop: number, halfBottom: number, skew = 0): [number, number][] => [
    [cx - halfBottom * s * buildScale, size],
    [cx - halfTop * s * buildScale + skew * s, shoulderY],
    [cx + halfTop * s * buildScale + skew * s, shoulderY],
    [cx + halfBottom * s * buildScale, size],
  ];

  /** A collar, so the torso is not a blank trapezoid. Key plate only. */
  const collar = (halfTop: number): string =>
    path(
      `M${n(cx - halfTop * s * buildScale * 0.9)} ${n(shoulderY + 4 * s)}` +
        ` L${n(cx)} ${n(shoulderY + 52 * s)}` +
        ` L${n(cx + halfTop * s * buildScale * 0.9)} ${n(shoulderY + 4 * s)}`,
      keyline(structure),
    );

  const neckBox = {
    x: cx - headR * 0.34,
    y: headY + headRy * 0.6,
    width: headR * 0.68,
    height: headRy * 0.66,
  };
  // The neck is the one part of the face always in shadow, so it prints as the
  // skin screen plus the shade screen. No darker skin colour is mixed.
  const neckPlate =
    tag('rect', { ...neckBox, fill: PAPER.card }) +
    tag('rect', { ...neckBox, fill: `url(#${P.skin})` }) +
    tag('rect', { ...neckBox, fill: `url(#${P.faceShade})` });
  /*
   * Only the two sides of the neck are cut into the key plate. Outlining the
   * whole box drew its top edge straight across the chin and mouth, because the
   * neck starts above the jaw line — a printer inking a face never drew the part
   * of the neck the head hides.
   */
  const neckKey = path(
    `M${n(neckBox.x)} ${n(g.chinY - headRy * 0.08)} V${n(neckBox.y + neckBox.height)}` +
      ` M${n(neckBox.x + neckBox.width)} ${n(g.chinY - headRy * 0.08)} V${n(neckBox.y + neckBox.height)}`,
    keyline(structure),
  );

  // --- head -------------------------------------------------------------
  const headEllipse = { cx, cy: headY, rx: headR, ry: headRy };
  /*
   * Shade is one screen on the lower-right of the face and nothing at all on the
   * upper-left. The bare paper IS the light. The old pass painted a lightened
   * crescent there, which is a rendered highlight and §8.3 now forbids it.
   */
  const shadeCrescent =
    `M${n(cx + headR)} ${n(headY)} a${n(headR)} ${n(headRy)} 0 0 1 ${n(-headR * 0.9)} ${n(headRy * 0.9)}` +
    ` a${n(headR * 1.3)} ${n(headRy * 1.3)} 0 0 0 ${n(headR * 0.9)} ${n(-headRy * 0.9)} Z`;

  const headPlate =
    platesOf(hair.back) +
    ellipse({ ...headEllipse, fill: PAPER.card }) +
    ellipse({ ...headEllipse, fill: `url(#${P.skin})` }) +
    path(shadeCrescent, { fill: `url(#${P.faceShade})` }) +
    platesOf(facialHairPieces(f, g)) +
    (f.hasEarring
      ? circle({ cx: cx - headR * 0.97, cy: headY + headRy * 0.26, r: 5 * s, fill: SPOT.yellow })
      : '') +
    platesOf(hair.front);

  const headKey =
    keysOf(hair.back) +
    ellipse({ ...headEllipse, ...keyline(outline) }) +
    // Nose: one short line, enough to stop the face reading as a mask.
    path(
      `M${n(cx)} ${n(headY - headRy * 0.02)} l${n(-headR * 0.09)} ${n(headRy * 0.22)} h${n(headR * 0.17)}`,
      keyline(structure * 0.8),
    ) +
    keysOf(facialHairPieces(f, g)) +
    (f.age === 'old'
      ? path(
          `M${n(cx - headR * 0.52)} ${n(g.browY - headRy * 0.12)} h${n(headR * 0.3)}` +
            ` M${n(cx + headR * 0.22)} ${n(g.browY - headRy * 0.12)} h${n(headR * 0.3)}`,
          { fill: 'none', stroke: INK.faded, 'stroke-width': detail },
        )
      : '') +
    (f.hasScar
      ? path(
          `M${n(cx + headR * 0.46)} ${n(headY - headRy * 0.46)} l${n(-headR * 0.14)} ${n(headRy * 0.62)}`,
          { fill: 'none', stroke: SPOT.red, 'stroke-width': structure, 'stroke-linecap': 'round' },
        )
      : '') +
    (f.hasEarring
      ? circle({ cx: cx - headR * 0.97, cy: headY + headRy * 0.26, r: 5 * s, ...keyline(detail) })
      : '') +
    keysOf(hair.front);

  // --- class props ------------------------------------------------------
  const clothed = (halfTop: number, halfBottom: number, skew = 0) => {
    const pts = shoulderPts(halfTop, halfBottom, skew);
    return {
      plate: polygon(pts, { fill: PAPER.aged }) + polygon(pts, { fill: `url(#${P.cloth})` }),
      key: polygon(pts, keyline(outline)),
    };
  };

  switch (kind) {
    case 'warrior': {
      const torso = clothed(152, 182);
      const padL = `M${n(cx - 152 * s * buildScale)} ${n(shoulderY)} a${n(48 * s)} ${n(30 * s)} 0 0 1 ${n(96 * s)} 0 Z`;
      const padR = `M${n(cx + 56 * s * buildScale)} ${n(shoulderY)} a${n(48 * s)} ${n(30 * s)} 0 0 1 ${n(96 * s)} 0 Z`;
      return {
        plate: neckPlate + torso.plate + path(padL, { fill: roleInk }) + path(padR, { fill: roleInk }) + headPlate,
        key:
          neckKey +
          torso.key +
          path(padL, keyline(outline)) +
          path(padR, keyline(outline)) +
          collar(152) +
          headKey,
      };
    }
    case 'rogue': {
      const torso = clothed(108, 132, 10);
      return {
        plate: neckPlate + torso.plate + headPlate,
        key: neckKey + torso.key + collar(108) + headKey,
      };
    }
    case 'mage': {
      const torso = clothed(104, 154);
      const staffX = cx + 150 * s;
      const staffTop = size * 0.16;
      const staff = { x: staffX - 6 * s, y: staffTop, width: 12 * s, height: size - staffTop };
      /*
       * The orb's radiance is rays, not a glow. A press that wanted something to
       * shine drew lines coming off it, because it had no way to print light.
       */
      const rays = Array.from({ length: 8 }, (_, i) => {
        const a = (i / 8) * Math.PI * 2;
        return path(
          `M${n(staffX + Math.cos(a) * 24 * s)} ${n(staffTop + Math.sin(a) * 24 * s)}` +
            ` L${n(staffX + Math.cos(a) * 40 * s)} ${n(staffTop + Math.sin(a) * 40 * s)}`,
          { fill: 'none', stroke: INK.key, 'stroke-width': detail, 'stroke-linecap': 'round' },
        );
      }).join('');
      return {
        plate:
          tag('rect', { ...staff, fill: PAPER.box }) +
          circle({ cx: staffX, cy: staffTop, r: 17 * s, fill: roleInk }) +
          neckPlate +
          torso.plate +
          headPlate,
        key:
          tag('rect', { ...staff, ...keyline(structure) }) +
          circle({ cx: staffX, cy: staffTop, r: 17 * s, ...keyline(structure) }) +
          rays +
          neckKey +
          torso.key +
          collar(104) +
          headKey,
      };
    }
    case 'priest': {
      const torso = clothed(136, 160);
      const stole = `M${n(cx - 136 * s * buildScale)} ${n(shoulderY)} a${n(136 * s * buildScale)} ${n(52 * s)} 0 0 0 ${n(272 * s * buildScale)} 0 Z`;
      const haloR = headR * 1.42;
      return {
        plate: neckPlate + torso.plate + path(stole, { fill: roleInk }) + headPlate,
        key:
          circle({ cx, cy: headY, r: haloR, fill: 'none', stroke: roleInk, 'stroke-width': outline * 1.5 }) +
          circle({ cx, cy: headY, r: haloR + outline, ...keyline(detail) }) +
          neckKey +
          torso.key +
          path(stole, keyline(outline)) +
          headKey,
      };
    }
    case 'npc': {
      const torso = clothed(124, 150);
      return {
        plate: neckPlate + torso.plate + headPlate,
        key: neckKey + torso.key + collar(124) + headKey,
      };
    }
  }
}

/**
 * Expression overlays act on the eye/mouth region only (Style Bible §5).
 *
 * These are key-plate lines and nothing else, so they never shift with the colour
 * plates — which is right: a printer changing an expression re-cut the black.
 */
function expressionOverlay(
  expression: NonNullable<PortraitOptions['expression']>,
  f: Features,
  g: FaceGeom,
): string {
  const { cx, eyeY, eyeDx, mouthY, browY, headR, s } = g;
  const strokeW = LINE.outline * s * 1.2;
  const ink = { fill: 'none', stroke: INK.key, 'stroke-width': strokeW, 'stroke-linecap': 'round' } as const;
  const w = headR * 0.3;

  const brows = path(
    `M${n(cx - eyeDx - w)} ${n(browY + f.browAngle * s)} l${n(w * 2)} ${n(-f.browAngle * 1.4 * s)}` +
      ` M${n(cx + eyeDx + w)} ${n(browY + f.browAngle * s)} l${n(-w * 2)} ${n(-f.browAngle * 1.4 * s)}`,
    { ...ink, 'stroke-width': strokeW * 0.85 },
  );

  /*
   * Lid arc plus a solid pupil. The arc alone is a closed eye, which is why every
   * portrait read as asleep; one filled dot of the key plate opens it, and a dot
   * is exactly what a press had to work with.
   */
  const eye = (dx: number, tilt: number): string =>
    path(`M${n(cx + dx - w)} ${n(eyeY + tilt * s)} q${n(w)} ${n(-w * 0.62)} ${n(w * 2)} ${n(-tilt * s)}`, ink) +
    circle({ cx: cx + dx, cy: eyeY + tilt * s * 0.5 + w * 0.18, r: w * 0.3, fill: INK.key });

  const mouth = (dy: number, curve: number, width = 0.34): string =>
    path(
      `M${n(cx - headR * width)} ${n(mouthY + dy * s)} q${n(headR * width)} ${n(curve * s)} ${n(headR * width * 2)} 0`,
      ink,
    );

  switch (expression) {
    case 'neutral':
      return brows + eye(-eyeDx, 0) + eye(eyeDx, 0) + mouth(0, 0);
    case 'warm':
      return brows + eye(-eyeDx, 3) + eye(eyeDx, 3) + mouth(-3, 16);
    case 'wary':
      return brows + eye(-eyeDx, -3) + eye(eyeDx, -3) + mouth(1, 0, 0.24);
    case 'hostile':
      return (
        path(
          `M${n(cx - eyeDx - w * 1.3)} ${n(browY - 6 * s)} l${n(w * 2.3)} ${n(12 * s)}` +
            ` M${n(cx + eyeDx + w * 1.3)} ${n(browY - 6 * s)} l${n(-w * 2.3)} ${n(12 * s)}`,
          ink,
        ) +
        eye(-eyeDx, -2) +
        eye(eyeDx, -2) +
        mouth(5, -14)
      );
    case 'broken':
      return (
        brows +
        eye(-eyeDx, 0) +
        eye(eyeDx, 0) +
        mouth(3, -9) +
        // The tear prints in the blue plate; it used to be a teal that exists
        // nowhere else in the game.
        path(`M${n(cx - eyeDx)} ${n(eyeY + 14 * s)} v${n(34 * s)}`, {
          fill: 'none',
          stroke: SPOT.blue,
          'stroke-width': strokeW * 0.8,
          'stroke-linecap': 'round',
        })
      );
  }
}

export function renderPortrait(opts: PortraitOptions): string {
  const size = opts.size ?? PORTRAIT_SPEC.size;
  const kind = opts.classOrRole ?? 'npc';
  const seed = opts.seed ?? opts.name;
  const f = deriveFeatures(seed, opts);
  const g = faceGeom(size, f);
  const s = size / 512;
  const roleInk = ROLE_INK[kind];

  /*
   * Screens carry every value in the portrait. Dot sizes scale with the canvas so
   * a 128px thumbnail still prints a screen rather than a smear.
   */
  const screens =
    paperGrain(P.grain, INK.key, `portrait:${seed}`) +
    // Skin is the spot plate, shading is the key plate: different angles, or the
    // black dots land on the orange ones and the face never gets darker (§2.2).
    halftonePattern(P.skin, SPOT.orange, HALFTONE[f.skin].dot * s, HALFTONE[f.skin].grid * s, SCREEN_ANGLE.spot) +
    halftonePattern(P.faceShade, INK.key, HALFTONE.mid.dot * s, HALFTONE.mid.grid * s, SCREEN_ANGLE.key) +
    halftonePattern(P.cloth, INK.key, HALFTONE.mid.dot * s, HALFTONE.mid.grid * s, SCREEN_ANGLE.key) +
    halftonePattern(P.hairScreen, f.hair, HALFTONE.mid.dot * s, HALFTONE.mid.grid * s, SCREEN_ANGLE.spotAlt) +
    halftonePattern(P.halo, roleInk, HALFTONE.light.dot * s, HALFTONE.light.grid * s, SCREEN_ANGLE.spot);

  /*
   * A screened roundel in the class ink, the way a printed portrait card sat its
   * subject on a spot-colour disc. It replaces two stacked translucent glows.
   *
   * No ring around it: outlined, the disc stopped being a background and became a
   * circle drawn across the sitter's shoulders.
   */
  const back =
    tag('rect', { width: size, height: size, fill: PAPER.board }) +
    circle({ cx: size / 2, cy: size * 0.38, r: size * 0.33, fill: `url(#${P.halo})` }) +
    tag('rect', { width: size, height: size, fill: `url(#${P.grain})` });

  // One offset for the whole subject (Style Bible §5.1): a face whose parts each
  // shift independently reads as broken, not as misprinted.
  const [dx, dy] = misregisterOffset(`portrait:${seed}`, size, MISREGISTRATION);
  const platePass = (body: string): string =>
    body === '' ? '' : group({ transform: `translate(${n(dx)} ${n(dy)})` }, body);

  const subject = opts.artHref
    ? tag('image', { href: opts.artHref, x: 0, y: 0, width: size, height: size, preserveAspectRatio: 'xMidYMid slice' })
    : (() => {
        const bust = bustSilhouette(kind, size, f, g);
        return platePass(bust.plate) + bust.key + expressionOverlay(opts.expression ?? 'neutral', f, g);
      })();

  const frame =
    agedEdges(size, size, PAPER.aged) +
    tag('rect', { x: 8 * s, y: 8 * s, width: size - 16 * s, height: size - 16 * s, ...keyline(LINE.outline * s * 1.4) }) +
    tag('rect', { x: 18 * s, y: 18 * s, width: size - 36 * s, height: size - 36 * s, fill: 'none', stroke: roleInk, 'stroke-width': LINE.detail * s });

  // A solid key bar with the type knocked out of it — how a press put a caption
  // on a picture. The bar used to be 72% black, which is a screen, not a bar.
  const nameplate = (opts.showName ?? true)
    ? tag('rect', { x: 18 * s, y: size - 66 * s, width: size - 36 * s, height: 48 * s, fill: INK.key }) +
      text(opts.name, {
        x: size / 2,
        y: size - 34 * s,
        'text-anchor': 'middle',
        'font-family': 'Noto Serif KR, Georgia, serif',
        'font-size': 26 * s,
        fill: PAPER.card,
      })
    : '';

  return svg(size, size, defs(screens) + back + subject + frame + nameplate);
}

/** FX are vector + CSS animation, never sprite sheets (Style Bible §7). */
export type FxKind = 'dust' | 'shimmer' | 'discoveryRing' | 'fateDebtPulse' | 'critBurst';

/**
 * FX print too. Opacity is gone from all of them: these sit over a paper board,
 * and the client animates them — fading an already-faint shape gave nothing to
 * see. Where an effect needs a value between paper and ink, it gets a screen.
 */
export function renderFx(kind: FxKind, size = 256, seed = 'fx'): string {
  const rng = new ArtRng(`fx:${kind}:${seed}`);
  const cx = size / 2;
  const cy = size / 2;
  const s = size / 256;

  switch (kind) {
    case 'dust':
      // Kicked-up board dust: paper shadow specks, each with a key dot inside so
      // they stay visible against cardstock.
      return svg(
        size,
        size,
        Array.from({ length: 14 }, () => {
          const x = cx + rng.range(-size * 0.4, size * 0.4);
          const y = cy + rng.range(-size * 0.1, size * 0.2);
          const r = rng.range(1.5, 5) * s;
          return (
            circle({ cx: x, cy: y, r, fill: PAPER.shadow }) +
            circle({ cx: x, cy: y, r, fill: 'none', stroke: INK.faded, 'stroke-width': LINE.detail * s })
          );
        }).join(''),
      );
    case 'shimmer':
      // A foil sweep, printed: bare paper bars ruled off with hairlines, which is
      // how a press said "this catches the light".
      return svg(
        size,
        size,
        Array.from({ length: 9 }, (_, i) => {
          const pts: [number, number][] = [
            [i * 40 * s - 60 * s, size],
            [i * 40 * s - 30 * s, size],
            [i * 40 * s + 80 * s, 0],
            [i * 40 * s + 50 * s, 0],
          ];
          return polygon(pts, { fill: PAPER.card }) + polygon(pts, { fill: 'none', stroke: INK.faded, 'stroke-width': LINE.detail * s });
        }).join(''),
      );
    case 'discoveryRing':
      return svg(
        size,
        size,
        [0.28, 0.38, 0.47]
          .map((r, i) =>
            circle({
              cx,
              cy,
              r: size * r,
              fill: 'none',
              stroke: i === 0 ? SPOT.yellow : INK.key,
              'stroke-width': (LINE.structure - i * 0.4) * s,
            }),
          )
          .join(''),
      );
    case 'fateDebtPulse':
      // Fate is the purple plate everywhere else in the game; it was a one-off
      // violet here, which meant the debt did not read as fate.
      return svg(
        size,
        size,
        defs(halftonePattern(P.fxScreen, SPOT.purple, HALFTONE.mid.dot * s, HALFTONE.mid.grid * s, HALFTONE_ANGLE)) +
          circle({ cx, cy, r: size * 0.42, fill: `url(#${P.fxScreen})` }) +
          circle({ cx, cy, r: size * 0.3, fill: 'none', stroke: SPOT.purple, 'stroke-width': LINE.structure * s }),
      );
    case 'critBurst':
      return svg(
        size,
        size,
        Array.from({ length: 12 }, (_, i) => {
          const a = (i / 12) * Math.PI * 2;
          const inner = i % 2 === 0 ? 0.18 : 0.24;
          const outer = i % 2 === 0 ? 0.44 : 0.36;
          return path(
            `M${n(cx + Math.cos(a) * size * inner)} ${n(cy + Math.sin(a) * size * inner)}` +
              ` L${n(cx + Math.cos(a) * size * outer)} ${n(cy + Math.sin(a) * size * outer)}`,
            {
              fill: 'none',
              stroke: i % 2 === 0 ? SPOT.red : INK.key,
              'stroke-width': LINE.structure * s,
              'stroke-linecap': 'round',
            },
          );
        }).join(''),
      );
  }
}

export const ITEM_SLOTS: readonly ItemSlot[] = ['weapon', 'armor', 'trinket', 'consumable', 'quest', 'material'];
export const FX_KINDS: readonly FxKind[] = ['dust', 'shimmer', 'discoveryRing', 'fateDebtPulse', 'critBurst'];
export const EXPRESSIONS = ['neutral', 'warm', 'wary', 'hostile', 'broken'] as const;

export function allIconAssets(): { id: string; svg: string }[] {
  const rarities: Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'];
  const out: { id: string; svg: string }[] = [];
  for (const slot of ITEM_SLOTS) {
    for (const rarity of rarities) {
      out.push({ id: `icon.${slot}.${rarity}`, svg: renderItemIcon({ slot, rarity }) });
    }
  }
  for (const kind of FX_KINDS) out.push({ id: `fx.${kind}`, svg: renderFx(kind) });
  return out;
}

export function allPortraitAssets(): { id: string; svg: string }[] {
  const out: { id: string; svg: string }[] = [];
  for (const cls of ['warrior', 'rogue', 'mage', 'priest'] as const) {
    out.push({ id: `portrait.class.${cls}`, svg: renderPortrait({ name: cls, classOrRole: cls }) });
  }
  for (const expression of EXPRESSIONS) {
    out.push({
      id: `portrait.expression.${expression}`,
      svg: renderPortrait({ name: expression, classOrRole: 'npc', expression }),
    });
  }
  return out;
}
